import Lesson from "../models/view/lessonPage/Lesson";
import Segment from "../models/view/lessonPage/Segment";
import Fingering from "../models/view/lessonPage/Fingering";
import Measure from "../models/view/lessonPage/Measure";
import LessonPlayerSettings from "../models/view/lessonPage/LessonPlayerSettings";
import {SegmentModeEnum} from "../models/view/lessonPage/SegmentModeEnum";
import {HandsTypeEnum} from "../models/view/lessonPage/HandsTypeEnum";
import ILessonResponse from "../client/models/lessonWithSingleModeSegments/ILessonResponse";
import ISegment from "../client/models/lessonWithSingleModeSegments/ISegment";
import IMeasure from "../client/models/lessonWithSingleModeSegments/IMeasure";
import IFingering from "../client/models/lessonWithSingleModeSegments/IFingering";
import {getSignInDataFromLocalStorage} from "../services/user/sign-in-data";
import NextLesson from "../models/view/lessonPage/NextLesson";
import {trimRight} from "../util/string-util";
import {generateUUID} from "../services/misc";
import {lessonPlayerStore} from "../redux/lesson/LessonPlayerStore";
import {NotationLayoutEnum} from "../models/view/lessonPage/NotationLayoutEnum";
import {getUserPreferencesForControlBar} from "../services/UserPreferencesForControlBarService";
import {baseUrlForWebsite} from "../client/PlaygroundSettings";

export function createLessonViewModelFromApiResponse(response: ILessonResponse, courseId: string | undefined): Lesson {
  if(!response) {
    return getEmptyLesson();
  }

  return new Lesson({
    id: response.id.toString(),
    title: response.title,
    segments: mapSegments(response, courseId),
    difficulty: response.difficulty.id,
    subTitle: response.subTitle,
    coverArtUrl: response.coverArtUrl,
    isFirstLessonOfSection: response.isFirstLessonOfSection,
    isLastLessonOfSection: response.isLastLessonOfSection,
    nextLesson: response.nextLesson
      ? new NextLesson({
        id: response.nextLesson.id.toString(),
        title: response.nextLesson.title,
        subTitle: response.nextLesson.subTitle,
        coverArtUrl: response.nextLesson.coverArtUrl
      })
      : undefined,
    section: response.section,
    nextSection: response.nextSection,
  });
}

function mapSegments(response: ILessonResponse, courseId: string | undefined): Segment[] {
  return response.segments.map((s, index ) => createSegmentViewModelFromApiResponse(s, response.id.toString(), response.difficulty.id, index, courseId));
}

function getEmptyLesson(): Lesson {
  return new Lesson({
    segments: [],
    id: '-1',
    title: '',
    difficulty: 0,
    subTitle: '',
    coverArtUrl: "",
    isFirstLessonOfSection: false,
    isLastLessonOfSection: false,
    section: undefined,
    nextSection: undefined
  });
}

/**
 * Creates the segment view model from the plx data returned from the api.
 * @param segment - segment plx returned as part of the /lessons/plx response.
 * @param lessonId - id of the lesson that the segment belongs to.
 * @param lessonDifficulty - the difficulty of the lesson is used to grade performances.
 * @param segmentIndex - Index of the segment in the list of segments for the lesson.
 * @param courseId - used to send request to backend to track which segment/lesson/course the user visited last.
 */
function createSegmentViewModelFromApiResponse(segment: ISegment, lessonId: string, lessonDifficulty: number, segmentIndex: number, courseId: string | undefined): Segment{
  const {
    id,
    mode: modeJson,
    measures: measuresJson,
    description,
    speedOptions: speedOptionsJson,
    hasVideo,
    fingerings: fingeringsJson,
    soundslice: soundsliceJson,
    name,
    hasUserCompletedTheSegment,
    userAccuracyScore,
    isFirstSegmentOfLesson,
    isLastSegmentOfLesson
  } = segment;
  const mode = createSegmentModeEnumFromString(modeJson); //server api will return 1 mode only in a coming change.
  const measures = measuresJson.map(createMeasureViewModelFromApiResponse);
  const descriptionHtml = convertSegmentDescriptionToHtml(description);
  const lessonPlayerSettings = createLessonPlayerSettingsFromApiResponse();
  const fingerings = fingeringsJson.map(createFingeringViewModelFromApiResponse);
  const tempo = getSegmentTempo(measures);
  const hands = segment.hands as HandsTypeEnum;
  const speedOptions = speedOptionsJson.map(so => { return {label: so.name, value: so.speed, recordingIndex: so.ssRecordingIndex, mp3Url: so.mp3Url };});
  const normalRecordingOption = speedOptions.find(s => s.label === "Normal");
  const normalRecordingIndex = normalRecordingOption?.recordingIndex || 0;
  const iframeRecordingIndex = hasVideo ? normalRecordingIndex : 0;
  const baseEmbedUrl = soundsliceJson.embedUrl;

  const soundsliceIframeUrl = createSoundsliceIframeUrl(lessonPlayerSettings, baseEmbedUrl, iframeRecordingIndex, segmentIndex);
  const recordingIndex = normalRecordingIndex;
  const url = createLessonUrl(lessonId, id, courseId, mode, undefined);
  const tabDisplayText = `${segmentIndex + 1}`;
  //@ts-ignore
  const isUserAccuracyScoreGreen = userAccuracyScore >= 80;
  return new Segment({
    lessonId,
    recordingIndex,
    descriptionHtml,
    name,
    tabDisplayText,
    id: id.toString(),
    fingerings,
    mode,
    url,
    soundsliceIframeUrl,
    measures,
    tempo,
    lessonPlayerSettings,
    baseEmbedUrl,
    hasVideo,
    lessonDifficulty,
    hands,
    speedOptions,
    shouldHideSegmentDescription: false,
    isUserAccuracyScoreGreen,
    userAccuracyScore,
    hasUserCompletedTheSegment,
    courseId,
    segmentIndex,
    isFirstSegmentOfLesson,
    isLastSegmentOfLesson
  });
}

//todo: make course id required
export function createLessonUrl(
  lessonId: string | number,
  segmentId: string | number | undefined,
  courseId: string | number | undefined,
  mode: string | undefined,
  referrer: string | undefined
){
  const segmentPart = segmentId !== undefined ? `/${segmentId}` : ``;
  const modePart = segmentId !== undefined && mode !== undefined ? `/${mode}` : ``;
  const courseIdPart = courseId !== undefined ? `?courseId=${courseId}` : ``;
  const queryStringSeparator = courseIdPart ? `&` : `?`;
  const referrerPart = referrer !== undefined ? `${queryStringSeparator}referrer=${referrer}`: ``;
  const url = `/lesson/${lessonId}${segmentPart}${modePart}${courseIdPart}${referrerPart}`;
  return url;
}

function createSegmentModeEnumFromString(modeString: string){
  switch (modeString) {
    case SegmentModeEnum.perform:
      return SegmentModeEnum.perform;
    case SegmentModeEnum.play:
      return SegmentModeEnum.play;
    case SegmentModeEnum.practice:
      return SegmentModeEnum.practice;
    case SegmentModeEnum.followAlong:
      return SegmentModeEnum.followAlong;
    default:
      return SegmentModeEnum.unspecified;
  }
}

/**
 * This will most likely be driven by backend configuration for each segment. For now, display everything.
 */
function createLessonPlayerSettingsFromApiResponse(){
  const isStaffNotationVisible = true;
  const userPreferencesForControlBar = getUserPreferencesForControlBar();
  const {
    isBirdsEyeKeyboardEnabled: isBirdsEyeKeyboardEnabledForNotation,
    isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly,
    isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation,
    isNoteNamesEnabled: noteNamesVisible,
    isFingeringEnabled: staffFingeringsVisible,
    areRhythmCountsEnabled: displayRhythmCounts,
  } = userPreferencesForControlBar;
  return new LessonPlayerSettings({
    staffFingeringsVisible,
    chordDiagramsVisible: true,
    chordNamesVisible: true,
    staffNotationVisible: isStaffNotationVisible,
    keyboard: {
      isVisibleForSegmentsWithNotationOnly: isBirdsEyeKeyboardEnabledForNotation,
      isVisibleForSegmentsWithVideoOnly: isBirdsEyeKeyboardEnabledForSegmentsWithVideoOnly,
      isVisibleForSegmentsWithBothVideoAndNotation: isBirdsEyeKeyboardEnabledForSegmentsWithBothVideoAndNotation
    },
    forceSideVideo: false,
    forceTopVideo: true,
    topVideoHeightPercent: 50,
    sideVideoWidthPercent: 50,
    horizontalScrollPlayheadFixedEnabled: true,
    noteNamesVisible,
    displayRhythmCounts,
    guid: generateUUID()
  });
}

/**
 * Creates a URL for the Soundslice iframe, referencing which notations (tab, standard, etc.)
 * should be displayed for the segment, as well as the embed url.
 */
export function createSoundsliceIframeUrl(
  lessonPlayerSettings: LessonPlayerSettings,
  baseEmbedUrl: string,
  recordingIndex: number,
  segmentIndex: number
){
  if(!baseEmbedUrl){ return `${baseUrlForWebsite}/no-embed-url-defined`; }
  const {
    staffNotationVisible,
    forceSideVideo,
    forceTopVideo,
    sideVideoWidthPercent,
    topVideoHeightPercent,
    horizontalScrollPlayheadFixedEnabled,

    // // Uncomment if ever used. Right now they can be incorrect and are overwritten by SS SDK calls after the SS iframe loads. See 2349023 for other related code.
    // chordNamesVisible,
    // chordDiagramsVisible,
    // staffFingeringsVisible,
  } = lessonPlayerSettings;
  // Don't subscribe to changes with useAppSelector or otherwise. We don't want the iframe to reload when zoom changes.
  // However, we do want the URL to have the initial zoom, to avoid unnecessary ui shifts.
  const hscrollTypeParam = horizontalScrollPlayheadFixedEnabled ? `&hscroll_type=2` : `&hscroll_type=0`;
  const hshrinkParam: string = `&hshrink=1`; // Scale the stave height when in horizontally scrolling notation layout
  const centerVideoParam = true ? `&cv=1` : `&cv=0`;
  const slowdownEngineParam = false ? `&slowdown=3` : ``;
  const brandingParam = `&branding=0`;
  const playTypeParam = '&play_type=2';//`&play_type=2`; //make it so looping only plays once.
  const getNotesFixParam = `&getnotesfix=1`;
  const deferParam = ``; // Load all audio indexes at once, so we don't have to call ssLoadAudioByIndex
  const recordingIndexParam = `&recording_idx=${recordingIndex}`;
  const soundsliceUserIdentifier = getSignInDataFromLocalStorage()?.soundsliceUserIdentifier || undefined;
  const soundsliceUserIdentifierParam = soundsliceUserIdentifier ? `&u=${soundsliceUserIdentifier}` : ``;

  const isHorizontalScrollEnabled = lessonPlayerStore.getAppliedNotationLayout() === NotationLayoutEnum.horizontal;

  // // Uncomment if ever used. Right now they can be incorrect and are overwritten by SS SDK calls after the SS iframe loads. See 2349023 for other related code.
  // const zoom = lessonPlayerStore.getPlaybackControlState().zoom;
  // const showChordsParam = chordDiagramsVisible && chordNamesVisible ? `` : `&show_chords=${Number(chordNamesVisible)}`; //don't supply a param if both should be visible. otherwise show chord names.  no option for diagrams alone.
  // const fingerNumbersParam = `&show_finger=${Number(staffFingeringsVisible)}`;
  // const zoomParam = `&zoom=${zoom}`;

  // https://www.soundslice.com/help/player-api/
  // https://www.soundslice.com/help/en/embedding/customizing/31/notation-options/
  const notationDisplayQueryStringParams = `show_staff=${Number(staffNotationVisible)}`
    // + `${zoomParam}${fingerNumbersParam}${showChordsParam}` // Uncomment if ever used. Right now they can be incorrect and are overwritten by SS SDK calls after the SS iframe loads. See 2349023 for other related code.
    + `&horiz=${Number(isHorizontalScrollEnabled)}&force_side_video=${Number(forceSideVideo)}`
    + `&force_top_video=${Number(forceTopVideo)}&side_video_width=${sideVideoWidthPercent}p`
    + `&narrow_video_height=${topVideoHeightPercent}p${hscrollTypeParam}&c=0${centerVideoParam}${recordingIndexParam}`
    + `&forceReactReloadForSegmentModes=${segmentIndex}${deferParam}`
    + `${soundsliceUserIdentifierParam}${slowdownEngineParam}${brandingParam}${playTypeParam}${getNotesFixParam}`
    + `&propvert=1` // Enables experimental ability to use proportional notation in vertical layout
    + `&settings=0` // Toggle access to the Soundslice settings panel
    + `&rsb=0`; // Toggle use of the slice author's stave breaks
  const lm = 'lm=0'; // Hides the context menu that appears when the user creates a loop.
  const videoFlippable = `flippable=0`; // disable video flip button

  const formattedBaseEmbedUrl = trimRight(baseEmbedUrl, `/`);
  return `https://www.soundslice.com${formattedBaseEmbedUrl}/?${lm}&controlbar=0&waveform=0&api=1&enable_synth=0&snap=-1&${notationDisplayQueryStringParams}&${videoFlippable}`;
}

/**
 * In theory, there can be many tempos per measure, and many measures per segment, however, in practice it's not often needed.
 * @param measures
 */
function getSegmentTempo(measures?: Measure[]){
  const defaultTempo = 119; //use 119 so it's easy to recognize when tempo is missing.
  if(!measures || measures.length <= 0){ return defaultTempo; }
  const firstMeasure = measures[0];
  return firstMeasure.tempo !== undefined ? firstMeasure.tempo : defaultTempo;
}

/**
 * Creates Measure view model based on data returned from the API.
 * @param measure - contains metadata about a given measure in a segment.
 */
function createMeasureViewModelFromApiResponse(measure: IMeasure){
  const { time: {beatType, beats}, bpm: tempo, seconds: durationSeconds } = measure;
  return new Measure({beats, beatType, tempo, durationSeconds});
}

/**
 * Creates fingering view model.
 * @param fingering
 */
function createFingeringViewModelFromApiResponse(fingering: IFingering): Fingering{
  const { color, finger, pitch, } = fingering;
  return new Fingering({ color, finger, pitch, });
}

/**
 * The API has \r and \n chars for new lines. This function replaces those newlines with <br/> html, so the description can be displayed as intended.
 * @param description - segment description
 */
function convertSegmentDescriptionToHtml(description: string = ""){
  if(!description){ return ""; }
  return description;
}
